import { useMutation } from '@apollo/client';
import { hasAnyTranslation } from '@assets/i18n/config';
import { datadogLogs } from '@datadog/browser-logs';
import type { TranslatedString } from '@interfaces/shared';
import { LanguageCodes } from '@onevesthq/ov-enums';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { buildUpdateClientGroupPortfolioInput } from '@ovComponents/4-module/widgets/portfolio/householdPortfolioMutationInput';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { enumValues } from '@utils/enumValues';
import { useToast } from 'hephaestus/web';
import { useContext, useEffect, useMemo, useState } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import {
  allocationOverrides,
  type EditedHouseholdModelPortfolio,
  initializeAllocationsProblem,
} from '../householdModelPortfolioDraft/householdAllocations';
import { isModelChangeProposalInFlight } from '../modelChangeProposal/modelChangeProposalErrors';
import {
  type InitializeHouseholdModelPortfolioOptions,
  InitializeHouseholdModelPortfolioVisual,
} from './initializeHouseholdModelPortfolio.visual';
import {
  InitializeLiveSecuritiesProvider,
  useInitializeLiveSecurities,
} from './initializeLiveSecuritiesContext';
import {
  SETUP_ENTITY_BOUND_MODEL,
  UPDATE_CLIENT_GROUP_PORTFOLIO,
} from './queries';
import { buildSetupEntityBoundModelInput } from './setupEntityBoundModelAdapter';

/**
 * The name product-service would give the copy, worked out here so the advisor
 * can see it and edit it instead of finding out afterwards.
 *
 * Mirrors `cloneModelPortfolio`: the same template, filled per language from
 * the parent's name in that language. It has to be mirrored rather than asked
 * for, because the copy does not exist until this step commits. A language the
 * parent has no name in is left empty — product-service leaves a bare "Copie
 * de " there, which is a name for nothing.
 *
 * Keep the `copyOf` strings in step with product-service's
 * `modelPortfolio.copyOfModelPortfolioName`; drift shows up as a copy named
 * something other than what the advisor was shown.
 */
const copyOfParentName = (
  parentName: TranslatedString | undefined,
  t: TFunction,
): TranslatedString | undefined => {
  if (!hasAnyTranslation(parentName)) return undefined;

  return Object.fromEntries(
    enumValues(LanguageCodes).map((language) => {
      const inLanguage = parentName?.[language]?.trim();
      return [
        language,
        inLanguage
          ? t('householdInitializeShells.modelName.copyOf', {
              lng: language,
              modelPortfolioName: inLanguage,
            })
          : '',
      ];
    }),
  ) as TranslatedString;
};

type SetupEntityBoundModelData = {
  setupEntityBoundModel?: {
    modelPortfolio?: { id?: string } | null;
    warnings?: string[] | null;
  } | null;
};

export type InitializeHouseholdModelPortfolioProps = {
  options: InitializeHouseholdModelPortfolioOptions;
  /**
   * Household ClientGroup id (the workflow completion `objectId`). It's both the
   * `boundObjectId` for `setupEntityBoundModel` and the `clientGroupId` for
   * `updateClientGroupPortfolio`. Falls back to
   * `workflowData.currentHouseholdClientGroupId`.
   */
  clientGroupId?: string;
  onNext: () => void;
  stepLoading?: boolean;
};

export const InitializeHouseholdModelPortfolio = (
  props: InitializeHouseholdModelPortfolioProps,
) => (
  <InitializeLiveSecuritiesProvider>
    <InitializeHouseholdModelPortfolioInner {...props} />
  </InitializeLiveSecuritiesProvider>
);

const InitializeHouseholdModelPortfolioInner = ({
  options,
  clientGroupId,
  onNext,
  stepLoading = false,
}: InitializeHouseholdModelPortfolioProps) => {
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { liveSecurities, flushPendingSecurities } =
    useInitializeLiveSecurities() ?? {};
  const { enqueueToast } = useToast();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation('workflowCompletions');

  const [setupEntityBoundModel, { loading: setupLoading }] =
    useMutation<SetupEntityBoundModelData>(SETUP_ENTITY_BOUND_MODEL);
  const [updateClientGroupPortfolio, { loading: attachLoading }] = useMutation(
    UPDATE_CLIENT_GROUP_PORTFOLIO,
  );

  // Held here rather than in the workflow context, which persists to the server
  // on every write — a keystroke is not a save. It goes into the context on the
  // way out, beside the clone id, once there is a committed copy to name.
  const [modelName, setModelName] = useState<TranslatedString | undefined>(
    workflowData.householdModelPortfolioName,
  );

  const suggestedName = useMemo(
    () =>
      copyOfParentName(
        workflowData.baseModelSnapshot?.details?.translatedName,
        t,
      ),
    [workflowData.baseModelSnapshot?.details?.translatedName, t],
  );

  // What the copy ends up called: the name in the box, or — when it was left
  // empty — the one product-service falls back to, which is the one suggested
  // above. Either way it is the name the steps after this one are editing.
  const committedName = hasAnyTranslation(modelName)
    ? modelName
    : suggestedName;

  // Seeded here rather than in `useState` because the parent is chosen on an
  // earlier step and can land in context after this one has mounted. Only ever
  // fills an untouched box: once the advisor has the field, including after
  // they empty it, what they left stands.
  useEffect(() => {
    setModelName((current) => current ?? suggestedName);
  }, [suggestedName]);

  // `modelPortfolioDetails` is shared with the generic create-model-portfolio
  // workflow (typed `any` there); it's a household draft here since that's the
  // shape `selectHouseholdParentModelPortfolio` writes to it.
  const draftModel = workflowData.modelPortfolioDetails as
    | EditedHouseholdModelPortfolio
    | undefined;

  const draftWithLiveAllocations = useMemo(() => {
    if (!draftModel) return undefined;
    if (options.allowAllocationEditing === false || !liveSecurities) {
      return draftModel;
    }
    return { ...draftModel, securities: liveSecurities };
  }, [draftModel, liveSecurities, options.allowAllocationEditing]);

  // Prefer the context-written household id, fall back to the completion
  // objectId threaded from `workflowCompletion.visual`.
  const householdClientGroupId =
    workflowData.currentHouseholdClientGroupId ?? clientGroupId;

  /**
   * Terminal commit (mint-late): create or reuse the entity-bound clone of the
   * selected parent, apply any staged allocation overrides, attach it to the
   * household with the chosen trading process, then advance.
   *
   * `setupEntityBoundModel` is always called — even when a clone id is already
   * in context — so a retry after a failed attach re-applies the latest
   * allocations instead of silently re-attaching stale weights. The mutation is
   * idempotent per household on the backend: it reuses the live copy when it
   * came from the same master, then re-applies customization.
   */
  const handleCommit = async () => {
    flushPendingSecurities?.();

    if (
      !draftWithLiveAllocations ||
      !workflowData.baseModelPortfolioId ||
      !householdClientGroupId ||
      !workflowData.entityOrganizationId
    ) {
      // Precondition failure (nothing selected / missing ids), not a failed
      // commit — use distinct, non-"try again" copy.
      enqueueToast(t('householdInitializeShells.commitMissingData'), {
        variant: 'error',
      });
      return;
    }

    // The Initialize button is disabled while a problem stands, so this is only
    // reached if the rows the table is showing and the ones context holds have
    // come apart. Say so rather than leaving the advisor with a dead button.
    const allocationsProblem =
      options.allowAllocationEditing === false
        ? undefined
        : initializeAllocationsProblem(
            draftWithLiveAllocations,
            workflowData.baseModelSnapshot,
          );
    if (allocationsProblem) {
      datadogLogs.logger.warn(
        'Household model portfolio initialize blocked on allocations',
        {
          clientGroupId: householdClientGroupId,
          baseModelPortfolioId: workflowData.baseModelPortfolioId,
          allocationsProblem,
        },
      );
      enqueueToast(t('householdInitializeShells.commitBlockedAllocations'), {
        variant: 'error',
      });
      return;
    }

    const allocations = allocationOverrides(
      draftWithLiveAllocations,
      workflowData.baseModelSnapshot,
    );
    // Held outside the attempt so a failed attach can still name the copy it
    // was reaching for, which is the one left minted but unattached.
    let cloneId: string | undefined;

    try {
      const input = buildSetupEntityBoundModelInput({
        baseModelPortfolioId: workflowData.baseModelPortfolioId,
        boundObjectId: householdClientGroupId,
        organizationId: workflowData.entityOrganizationId,
        // Sent whether the household is being set up for the first time or
        // moved onto another model, because product-service only acts on it
        // in the one case that needs it: a live copy taken from a different
        // master is retired and a fresh one minted. A household with no copy,
        // or one already on this master, is unaffected — the copy it has is
        // reused with its customizations intact.
        replaceExistingBase: true,
        // What the box says is what the copy is called. Left out only when it
        // is empty in every language, which leaves the naming to
        // product-service exactly as it was before this field existed.
        ...(hasAnyTranslation(modelName) ? { translatedName: modelName } : {}),
        allocations,
      });
      const { data } = await setupEntityBoundModel({
        // The refusals this can come back with are worded for the advisor
        // below; the global handler would toast the raw message first.
        variables: { input, skipErrorHandler: true },
      });
      cloneId = data?.setupEntityBoundModel?.modelPortfolio?.id ?? undefined;
      if (!cloneId) {
        throw new Error('setupEntityBoundModel returned no model portfolio');
      }
      // Persist the clone id via the functional updater (merges into the
      // latest context). `setWorkflowData` already persists to the server,
      // so we must NOT follow it with `updateWorkflowContext()` — that would
      // re-persist the pre-mint context from this render's closure and drop
      // the clone id we need for retries and reloads.
      await setWorkflowData((previousData) => ({
        ...previousData,
        entityBoundModelPortfolioId: cloneId,
        ...(hasAnyTranslation(modelName)
          ? { householdModelPortfolioName: modelName }
          : {}),
        // The draft was staged from the parent and still carries its name. Now
        // that the copy exists it is the copy the later steps are editing, so
        // the draft has to say what the copy is called — otherwise an IPS step
        // in the same workflow opens on the parent's name and proposes it
        // back, undoing the naming this step just did.
        ...(committedName
          ? {
              modelPortfolioDetails: {
                ...previousData.modelPortfolioDetails,
                translatedName: committedName,
              },
            }
          : {}),
      }));
      if ((data?.setupEntityBoundModel?.warnings ?? []).length > 0) {
        enqueueToast(t('householdInitializeShells.commitWarning'), {
          variant: 'warning',
        });
      }

      await updateClientGroupPortfolio({
        variables: {
          input: buildUpdateClientGroupPortfolioInput({
            clientGroupId: householdClientGroupId,
            financialProductId: cloneId,
            tradingProcess: workflowData.tradingProcess,
          }),
        },
      });

      sendAnalytic(ovAnalyticsEvents.householdInitializeCommit);
      onNext();
    } catch (error) {
      datadogLogs.logger.error(
        'Household model portfolio initialize commit failed',
        {
          clientGroupId: householdClientGroupId,
          baseModelPortfolioId: workflowData.baseModelPortfolioId,
          entityBoundModelPortfolioId:
            cloneId ?? workflowData.entityBoundModelPortfolioId,
          sentAllocations: !!allocations,
          sentGroupWeights: !!allocations?.assetClassBranches,
          sentModelName: hasAnyTranslation(modelName),
        },
        error instanceof Error ? error : undefined,
      );
      enqueueToast(
        isModelChangeProposalInFlight(error)
          ? t('householdInitializeShells.commitProposalPending')
          : t('householdInitializeShells.commitError'),
        { variant: 'error' },
      );
    }
  };

  return (
    <InitializeHouseholdModelPortfolioVisual
      options={options}
      loading={stepLoading || setupLoading || attachLoading}
      onNext={handleCommit}
      draftModel={draftWithLiveAllocations}
      baseModelSnapshot={workflowData.baseModelSnapshot}
      // Undefined means untouched and unsuggested — a parent with no name of
      // its own. The advisor still gets an empty box to name the copy in.
      modelName={modelName ?? getLanguageDefaults()}
      onModelNameChange={setModelName}
      summary={{
        parentModelName:
          workflowData.baseModelSnapshot?.details?.translatedName,
        tradingProcess: workflowData.tradingProcess,
      }}
    />
  );
};
